/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import type { CacheEntry } from "./types";
import { safeLocalStorage } from "@/helper/safeLocalStorage";

// Constants

export const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes
export const DEFAULT_RETRY_COUNT = 3;
export const DEFAULT_RETRY_DELAY = 1000; // 1 second
export const DEFAULT_TIMEOUT = 30000; // 30 seconds
export const MAX_RETRY_DELAY = 10000; // 10 seconds

const CACHE_PREFIX = "ds_api_cache_";
const MEMORY_CACHE_MAX_SIZE = 100;

// In-memory LRU cache with TTL

export class MemoryCache {
  private cache: Map<string, CacheEntry<unknown>> = new Map();
  private maxSize: number;

  constructor(maxSize: number = MEMORY_CACHE_MAX_SIZE) {
    this.maxSize = maxSize;
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    // B-10: True LRU — delete and re-insert so this key moves to the end of
    // Map's insertion order. When eviction runs, the first key (least recently
    // used) is removed, not the oldest-inserted (which was FIFO behaviour).
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.data;
  }

  set<T>(key: string, data: T, ttl: number): void {
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };

    this.cache.set(key, entry);
  }

  delete(key: string): void {
    this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }
    return true;
  }
}

// localStorage-backed cache with TTL

export class LocalStorageCache {
  private prefix: string;

  constructor(prefix: string = CACHE_PREFIX) {
    this.prefix = prefix;
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  get<T>(key: string): T | null {
    try {
      const stored = safeLocalStorage.getItem(this.getKey(key));
      if (!stored) return null;

      const entry: CacheEntry<T> = JSON.parse(stored);

      if (Date.now() > entry.expiresAt) {
        safeLocalStorage.removeItem(this.getKey(key));
        return null;
      }

      return entry.data;
    } catch {
      return null;
    }
  }

  set<T>(key: string, data: T, ttl: number): void {
    try {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttl,
      };
      safeLocalStorage.setItem(this.getKey(key), JSON.stringify(entry));
    } catch {
      // safeLocalStorage might be full or disabled — silent degradation
    }
  }

  delete(key: string): void {
    try {
      safeLocalStorage.removeItem(this.getKey(key));
    } catch {
      // Ignore errors
    }
  }

  clear(): void {
    try {
      // NF-2: Use safeLocalStorage.removeItem for each matching key.
      // Object.keys(localStorage) is safe here — we only use it to enumerate keys,
      // not to read values directly. safeLocalStorage.removeItem handles SSR/disabled storage.
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          safeLocalStorage.removeItem(key);
        }
      });
    } catch {
      // Ignore errors
    }
  }
}

// Deduplicates concurrent requests for the same resource

export class RequestDeduplicator {
  private pendingRequests: Map<string, Promise<unknown>> = new Map();

  async dedupe<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key) as Promise<T>;
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key);
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  isPending(key: string): boolean {
    return this.pendingRequests.has(key);
  }
}
